import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Autocomplete,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  Grid as MuiGrid,
} from '@mui/material';
import { Save as SaveIcon, Cancel as CancelIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { groupService, GroupCriteriaOptions } from '../../services/groupService';

// Use Grid as a custom component to fix MUI version issues
const Grid = MuiGrid;

interface FormData {
  name: string;
  description: string;
  group_type: 'manual' | 'auto';
  is_active: boolean;
  auto_criteria: Record<string, any>;
}

interface AutoCriteria {
  date_from?: string;
  date_to?: string;
  properties?: Array<{ id: number; name: string }>;
  agents?: Array<{ id: number; name: string }>;
  statuses?: string[];
  countries?: string[];
  states?: string[];
  cities?: string[];
  market_segments?: string[];
  booking_types?: string[];
}

const CreateGroup: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [criteriaOptions, setCriteriaOptions] = useState<GroupCriteriaOptions | null>(null);

  const [formData, setFormData] = useState<FormData>({
    name: '',
    description: '',
    group_type: 'manual',
    is_active: true,
    auto_criteria: {}
  });

  const [groupType, setGroupType] = useState<'manual' | 'auto'>('manual');
  const [autoCriteria, setAutoCriteria] = useState<AutoCriteria>({});

  useEffect(() => {
    if (groupType === 'auto') {
      loadCriteriaOptions();
    }
  }, [groupType]);

  const loadCriteriaOptions = async () => {
    try {
      const response = await groupService.getGroupCriteriaOptions();
      setCriteriaOptions(response.data);
    } catch (err) {
      setError('Failed to load criteria options');
      console.error('Error loading criteria options:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError('Group name is required');
      return;
    }

    try {
      setLoading(true);
      
      const groupData = {
        ...formData,
        group_type: groupType,
        auto_criteria: groupType === 'auto' ? autoCriteria : {}
      };

      await groupService.createGroup(groupData);
      setSuccess('Group created successfully');
      
      // Navigate back to groups list after a short delay
      setTimeout(() => {
        navigate('/groups');
      }, 1500);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to create group');
      console.error('Error creating group:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCriteriaChange = (field: string, value: any) => {
    setAutoCriteria(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Typography variant="h4" component="h1" gutterBottom>
        Create New Group
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Create a new customer group for targeted marketing campaigns
      </Typography>

      {/* Form */}
      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          {/* Basic Information */}
          <Grid style={{ gridColumn: 'span 8' }}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Basic Information
              </Typography>
              
              <Stack spacing={3}>
                <TextField
                  label="Group Name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  required
                  fullWidth
                />

                <TextField
                  label="Description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  multiline
                  rows={3}
                  fullWidth
                />

                <FormControl fullWidth>
                  <InputLabel>Group Type</InputLabel>
                  <Select
                    value={groupType}
                    onChange={(e) => setGroupType(e.target.value as 'manual' | 'auto')}
                    label="Group Type"
                  >
                    <MenuItem value="manual">Manual</MenuItem>
                    <MenuItem value="auto">Automatic</MenuItem>
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.is_active ? 'true' : 'false'}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.value === 'true' }))}
                    label="Status"
                  >
                    <MenuItem value="true">Active</MenuItem>
                    <MenuItem value="false">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
            </Paper>
          </Grid>

          {/* Auto Criteria (only for auto groups) */}
          {groupType === 'auto' && (
            <Grid style={{ gridColumn: 'span 12' }}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Automatic Grouping Criteria
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Define criteria to automatically populate this group with matching reservations
                </Typography>

                <Grid container spacing={3}>
                  {/* Date Range */}
                  <Grid style={{ gridColumn: 'span 12' }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Date Range
                    </Typography>
                    <Stack direction="row" spacing={2}>
                      <TextField
                        label="From Date"
                        type="date"
                        value={autoCriteria.date_from || ''}
                        onChange={(e) => handleCriteriaChange('date_from', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                      />
                      <TextField
                        label="To Date"
                        type="date"
                        value={autoCriteria.date_to || ''}
                        onChange={(e) => handleCriteriaChange('date_to', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Stack>
                  </Grid>

                  {/* Properties */}
                  <Grid style={{ gridColumn: 'span 6' }}>
                    <Autocomplete
                      multiple
                      options={criteriaOptions?.properties || []}
                      getOptionLabel={(option) => option.name}
                      value={autoCriteria.properties || []}
                      onChange={(_, value) => handleCriteriaChange('properties', value)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Properties"
                          placeholder="Select properties..."
                        />
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => {
                          const { key, ...tagProps } = getTagProps({ index });
                          return (
                            <Chip
                              key={`property-${option.id}`}
                              label={option.name}
                              size="small"
                              {...tagProps}
                            />
                          );
                        })
                      }
                    />
                  </Grid>

                  {/* Agents */}
                  <Grid style={{ gridColumn: 'span 6' }}>
                    <Autocomplete
                      multiple
                      options={criteriaOptions?.agents || []}
                      getOptionLabel={(option) => option.name}
                      value={autoCriteria.agents || []}
                      onChange={(_, value) => handleCriteriaChange('agents', value)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Agents"
                          placeholder="Select agents..."
                        />
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => {
                          const { key, ...tagProps } = getTagProps({ index });
                          return (
                            <Chip
                              key={`agent-${option.id}`}
                              label={option.name}
                              size="small"
                              {...tagProps}
                            />
                          );
                        })
                      }
                    />
                  </Grid>

                  {/* Reservation Status */}
                  <Grid style={{ gridColumn: 'span 6' }}>
                    <Autocomplete
                      multiple
                      options={criteriaOptions?.statuses || []}
                      value={autoCriteria.statuses || []}
                      onChange={(_, value) => handleCriteriaChange('statuses', value)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Reservation Status"
                          placeholder="Select statuses..."
                        />
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => {
                          const { key, ...tagProps } = getTagProps({ index });
                          return (
                            <Chip
                              key={`status-${option}`}
                              label={option}
                              size="small"
                              {...tagProps}
                            />
                          );
                        })
                      }
                    />
                  </Grid>

                  {/* Market Segments */}
                  <Grid style={{ gridColumn: 'span 6' }}>
                    <Autocomplete
                      multiple
                      options={criteriaOptions?.market_segments || []}
                      value={autoCriteria.market_segments || []}
                      onChange={(_, value) => handleCriteriaChange('market_segments', value)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Market Segments"
                          placeholder="Select market segments..."
                        />
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => {
                          const { key, ...tagProps } = getTagProps({ index });
                          return (
                            <Chip
                              key={`segment-${option}`}
                              label={option}
                              size="small"
                              {...tagProps}
                            />
                          );
                        })
                      }
                    />
                  </Grid>

                  {/* Guest Locations */}
                  <Grid style={{ gridColumn: 'span 4' }}>
                    <Autocomplete
                      multiple
                      options={criteriaOptions?.countries || []}
                      value={autoCriteria.countries || []}
                      onChange={(_, value) => handleCriteriaChange('countries', value)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Countries"
                          placeholder="Select countries..."
                        />
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => {
                          const { key, ...tagProps } = getTagProps({ index });
                          return (
                            <Chip
                              key={`country-${option}`}
                              label={option}
                              size="small"
                              {...tagProps}
                            />
                          );
                        })
                      }
                    />
                  </Grid>

                  <Grid style={{ gridColumn: 'span 4' }}>
                    <Autocomplete
                      multiple
                      options={criteriaOptions?.states || []}
                      value={autoCriteria.states || []}
                      onChange={(_, value) => handleCriteriaChange('states', value)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="States"
                          placeholder="Select states..."
                        />
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => {
                          const { key, ...tagProps } = getTagProps({ index });
                          return (
                            <Chip
                              key={`state-${option}`}
                              label={option}
                              size="small"
                              {...tagProps}
                            />
                          );
                        })
                      }
                    />
                  </Grid>

                  <Grid style={{ gridColumn: 'span 4' }}>
                    <Autocomplete
                      multiple
                      options={criteriaOptions?.cities || []}
                      value={autoCriteria.cities || []}
                      onChange={(_, value) => handleCriteriaChange('cities', value)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Cities"
                          placeholder="Select cities..."
                        />
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => {
                          const { key, ...tagProps } = getTagProps({ index });
                          return (
                            <Chip
                              key={`city-${option}`}
                              label={option}
                              size="small"
                              {...tagProps}
                            />
                          );
                        })
                      }
                    />
                  </Grid>

                  {/* Booking Types */}
                  <Grid style={{ gridColumn: 'span 12' }}>
                    <Autocomplete
                      multiple
                      options={criteriaOptions?.booking_types || []}
                      value={autoCriteria.booking_types || []}
                      onChange={(_, value) => handleCriteriaChange('booking_types', value)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Booking Types"
                          placeholder="Select booking types..."
                        />
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => {
                          const { key, ...tagProps } = getTagProps({ index });
                          return (
                            <Chip
                              key={`booking-${option}`}
                              label={option}
                              size="small"
                              {...tagProps}
                            />
                          );
                        })
                      }
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          )}

          {/* Actions */}
          <Grid style={{ gridColumn: 'span 12' }}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                onClick={() => navigate('/groups')}
                disabled={loading}
              >
                <CancelIcon sx={{ mr: 1 }} />
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
              >
                {loading ? (
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                ) : (
                  <SaveIcon sx={{ mr: 1 }} />
                )}
                Create Group
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Success/Error Snackbars */}
      <Snackbar
        open={!!success}
        autoHideDuration={4000}
        onClose={() => setSuccess(null)}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={4000}
        onClose={() => setError(null)}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CreateGroup; 